/**
 * Contact ROI workflow helpers — revoke and lookup only.
 *
 * New ROI authorizations are assigned from the chart via the Coordination of Care
 * template (Add other document → client signature). This module retains revoke
 * and questionnaire-response lookup used by contact ROI view modals.
 */
import type { MedplumClient } from '@medplum/core';
import type { Consent, QuestionnaireResponse } from '@medplum/fhirtypes';
import { ROI_CONSENT_REF_EXT } from '@unify/fhir-model/constants';
import { executeTaskMutation } from './taskLifecycleBots';
import { isRoiConsent } from '@unify/fhir-model/resources/consent';
import { setChartDocumentStatus } from './chart/chartDocumentWorkflow';

export { isContactRoiQuestionnaireResponse } from './chart/chartDocumentWorkflow';

export function findContactRoiQuestionnaireResponse(
  responses: QuestionnaireResponse[],
  consentId: string,
): QuestionnaireResponse | undefined {
  const consentRef = `Consent/${consentId}`;
  return responses.find((qr) =>
    qr.extension?.some(
      (e) =>
        e.url === ROI_CONSENT_REF_EXT &&
        e.valueReference?.reference === consentRef,
    ),
  );
}

export function isPendingContactRoiConsent(consent: Consent): boolean {
  return isRoiConsent(consent) && consent.status === 'draft';
}

export function isRevocableContactRoiConsent(consent: Consent): boolean {
  return isRoiConsent(consent) && (consent.status === 'draft' || consent.status === 'active');
}

export interface RevokeContactRoiParams {
  patientId: string;
  consentId: string;
}

export interface RevokeContactRoiResult {
  consent: Consent;
  questionnaireResponse?: QuestionnaireResponse;
  cancelledTaskIds: string[];
  wasPending: boolean;
}

async function cancelOpenRoiClientTasks(
  medplum: MedplumClient,
  questionnaireResponseId: string,
): Promise<string[]> {
  const tasks = await medplum.searchResources('Task', {
    focus: `QuestionnaireResponse/${questionnaireResponseId}`,
    _count: '50',
  });
  const cancelledTaskIds: string[] = [];
  for (const task of tasks) {
    if (task.status === 'completed' || task.status === 'cancelled' || !task.id) {
      continue;
    }
    await executeTaskMutation(medplum, { action: 'cancel', taskId: task.id });
    cancelledTaskIds.push(task.id);
  }
  return cancelledTaskIds;
}

/** Provider revokes a contact ROI — pending (draft) or signed (active). */
export async function revokeContactRoi(
  medplum: MedplumClient,
  params: RevokeContactRoiParams,
): Promise<RevokeContactRoiResult> {
  const { patientId, consentId } = params;

  const consent = await medplum.readResource('Consent', consentId);
  if (!isRevocableContactRoiConsent(consent)) {
    throw new Error('This ROI cannot be revoked.');
  }

  const wasPending = consent.status === 'draft';

  const patientResponses = await medplum.searchResources('QuestionnaireResponse', {
    subject: `Patient/${patientId}`,
    _count: '100',
    _sort: '-_lastUpdated',
  });
  const questionnaireResponse = findContactRoiQuestionnaireResponse(patientResponses, consentId);

  const updatedConsent = await medplum.updateResource<Consent>({
    ...consent,
    status: wasPending ? 'rejected' : 'inactive',
  });

  let updatedQr: QuestionnaireResponse | undefined;
  let cancelledTaskIds: string[] = [];

  if (questionnaireResponse?.id) {
    if (wasPending) {
      updatedQr = await medplum.updateResource<QuestionnaireResponse>(
        setChartDocumentStatus(questionnaireResponse, 'cancelled'),
      );
    }
    cancelledTaskIds = await cancelOpenRoiClientTasks(medplum, questionnaireResponse.id);
  }

  medplum.invalidateSearches('Consent');
  medplum.invalidateSearches('QuestionnaireResponse');
  medplum.invalidateSearches('Task');

  return {
    consent: updatedConsent,
    questionnaireResponse: updatedQr,
    cancelledTaskIds,
    wasPending,
  };
}

/** @deprecated Use {@link revokeContactRoi}. */
export const revokePendingContactRoi = revokeContactRoi;

export type RevokePendingContactRoiParams = RevokeContactRoiParams;
export type RevokePendingContactRoiResult = RevokeContactRoiResult;
